import React, { useState } from 'react';
import { MoreVertical, Phone, Mail, Calendar, User } from 'lucide-react';
import { candidateAPI } from '../../../services';

// TA-only mutating actions. Manager/HOD only ever see this card read-only from
// the shared Kanban - their feedback happens exclusively on their own dashboard.
const CandidateCard = ({
  candidate,
  currentStage,
  canManage,
  onSelect,
  onRefresh
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQuickAction, setShowQuickAction] = useState(null);
  const [quickActionData, setQuickActionData] = useState({});
  const [interviewers, setInterviewers] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short'
    });
  };

  const openScheduleForm = async (roundType) => {
    setError('');
    setQuickActionData({ roundType });
    setShowQuickAction('schedule_interview');
    try {
      const res = await candidateAPI.getInterviewers(roundType);
      if (res.status === 'success') {
        setInterviewers(res.data.interviewers || []);
      }
    } catch (err) {
      setError('Failed to load interviewers');
    }
  };

  const handleQuickAction = async (actionType, data) => {
    try {
      setLoading(true);
      setError('');

      switch (actionType) {
        case 'shortlist':
          await candidateAPI.shortlist(candidate._id, data);
          break;
        case 'schedule_interview':
          await candidateAPI.scheduleInterview(candidate._id, data);
          break;
        case 'move_to_ctc':
          await candidateAPI.moveToCTC(candidate._id, data);
          break;
        default:
          break;
      }

      setShowQuickAction(null);
      setQuickActionData({});
      onRefresh();
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div
          className="flex-1 cursor-pointer"
          onClick={onSelect}
        >
          <h4 className="font-semibold text-gray-900 hover:text-blue-600">
            {candidate.name}
          </h4>
          <p className="text-sm text-gray-500">
            {candidate.experience?.years || 0} yrs exp
          </p>
        </div>

        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-6 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 w-40">
              <button
                onClick={() => { onSelect(); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                View Profile
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="space-y-1 mb-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Mail className="w-3 h-3" />
          <span className="truncate">{candidate.email}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Phone className="w-3 h-3" />
          <span>{candidate.phone}</span>
        </div>
      </div>

      {/* Skills */}
      {candidate.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {candidate.skills.slice(0, 3).map((skill, i) => (
            <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
              {skill}
            </span>
          ))}
          {candidate.skills.length > 3 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
              +{candidate.skills.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Stage-specific Info */}
      {(currentStage === 'manager_review' || currentStage === 'hod_review') && candidate.interviews?.length > 0 && (
        <div className="text-xs text-gray-600 bg-purple-50 rounded p-2 mb-2">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(candidate.interviews[candidate.interviews.length - 1]?.scheduledAt)}</span>
          </div>
        </div>
      )}

      {currentStage === 'ctc_round' && candidate.ctcDiscussion && (
        <div className="text-xs text-gray-600 bg-yellow-50 rounded p-2 mb-2">
          {candidate.ctcDiscussion.negotiatedCTC?.amount && (
            <div>Negotiated: ₹{candidate.ctcDiscussion.negotiatedCTC.amount.toLocaleString()}</div>
          )}
        </div>
      )}

      {currentStage === 'offer_sent' && candidate.offer && (
        <div className="text-xs text-gray-600 bg-indigo-50 rounded p-2 mb-2">
          <div>CTC: ₹{candidate.offer.offeredCTC?.amount?.toLocaleString() || 'N/A'}</div>
          {candidate.offer.joiningDate && (
            <div>Join: {formatDate(candidate.offer.joiningDate)}</div>
          )}
        </div>
      )}

      {/* Source Label */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400 capitalize">{candidate.source}</span>
        <span className="text-gray-400">{formatDate(candidate.createdAt)}</span>
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-600">{error}</div>
      )}

      {/* TA-only stage-specific actions. Manager/HOD never get scheduling or
          move-forward controls - they only give feedback, from their own dashboard. */}
      {canManage && currentStage === 'sourced' && (
        <button
          onClick={() => handleQuickAction('shortlist', {})}
          disabled={loading}
          className="w-full mt-2 py-1.5 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 disabled:opacity-50"
        >
          Shortlist
        </button>
      )}

      {canManage && currentStage === 'shortlisted' && (
        <button
          onClick={() => openScheduleForm('manager')}
          className="w-full mt-2 py-1.5 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100"
        >
          Schedule Manager Interview
        </button>
      )}

      {canManage && currentStage === 'manager_selected' && (
        <button
          onClick={() => openScheduleForm('hod')}
          className="w-full mt-2 py-1.5 text-xs bg-fuchsia-50 text-fuchsia-600 rounded hover:bg-fuchsia-100"
        >
          Schedule HOD Interview
        </button>
      )}

      {canManage && currentStage === 'hod_selected' && (
        <button
          onClick={() => handleQuickAction('move_to_ctc', {})}
          disabled={loading}
          className="w-full mt-2 py-1.5 text-xs bg-yellow-50 text-yellow-600 rounded hover:bg-yellow-100 disabled:opacity-50"
        >
          Move to CTC Round
        </button>
      )}

      {/* Schedule Interview Modal */}
      {showQuickAction === 'schedule_interview' && (
        <ScheduleInterviewForm
          roundType={quickActionData.roundType}
          interviewers={interviewers}
          data={quickActionData}
          onChange={setQuickActionData}
          onSubmit={() => handleQuickAction('schedule_interview', {
            roundType: quickActionData.roundType,
            interviewerId: quickActionData.interviewerId,
            scheduledAt: quickActionData.scheduledAt,
            type: quickActionData.mode === 'online' ? 'video' : 'in_person',
            meetingLink: quickActionData.link,
            duration: 60
          })}
          onCancel={() => { setShowQuickAction(null); setQuickActionData({}); }}
          loading={loading}
        />
      )}
    </div>
  );
};

const ScheduleInterviewForm = ({ roundType, interviewers, data, onChange, onSubmit, onCancel, loading }) => {
  const handleChange = (name, value) => onChange({ ...data, [name]: value });
  const roundLabel = roundType === 'hod' ? 'HOD' : 'Manager';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4" onClick={e => e.stopPropagation()}>
        <h4 className="font-semibold text-gray-900 mb-3">Schedule {roundLabel} Interview</h4>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Interviewer</label>
            <select
              value={data.interviewerId || ''}
              onChange={(e) => handleChange('interviewerId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Select {roundLabel}...</option>
              {interviewers.map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Date & Time</label>
            <input
              type="datetime-local"
              value={data.scheduledAt || ''}
              onChange={(e) => handleChange('scheduledAt', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Mode</label>
            <select
              value={data.mode || ''}
              onChange={(e) => handleChange('mode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Select...</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Meeting Link (if online)</label>
            <input
              type="text"
              value={data.link || ''}
              onChange={(e) => handleChange('link', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={onSubmit}
            disabled={loading || !data.interviewerId || !data.scheduledAt}
            className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Schedule'}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateCard;
